/**
 * V116 — Beta feedback widget.
 *
 * Floating button bottom-right of every dashboard page. Renders only
 * when `beta_feedback_enabled = true` (probed once via /api/feedback/config
 * on mount). Click → slide-over panel with category + title + description
 * + optional screenshot. Submit → POST /api/feedback. Server email-
 * notifies admin and stores screenshot in S3 if attached.
 *
 * Widget is NOT mounted on admin pages or public pages — see
 * DashboardLayout for the gate.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Bug,
  Camera,
  Check,
  ImagePlus,
  Lightbulb,
  Loader2,
  MessageSquare,
  HelpCircle,
  Send,
  X as XIcon,
} from 'lucide-react';
import { feedbackApi } from '@/lib/api';
import { useWorkspaceContext } from '@/components/WorkspaceProvider';
import { Button } from '@/components/ui/Button';

type Category = 'bug' | 'feature_request' | 'general' | 'question';

const CATEGORY_META: Record<Category, { icon: typeof Bug }> = {
  bug: { icon: Bug },
  feature_request: { icon: Lightbulb },
  general: { icon: MessageSquare },
  question: { icon: HelpCircle },
};

export function BetaFeedbackWidget() {
  const { t } = useTranslation();
  const location = useLocation();
  const { activeWorkspace } = useWorkspaceContext();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null);
  const [screenshotName, setScreenshotName] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Probe enabled-state once on mount. Failures default to disabled
  // (silent no-op rather than rendering a broken button).
  useEffect(() => {
    feedbackApi
      .config()
      .then((r) => setEnabled(r.enabled))
      .catch(() => setEnabled(false));
  }, []);

  const submit = useMutation({
    mutationFn: () =>
      feedbackApi.submit(
        {
          category,
          title,
          description,
          pageUrl: location.pathname + location.search,
          screenshotBase64: screenshotBase64 ?? undefined,
        },
        activeWorkspace?.id,
      ),
    onSuccess: () => {
      setSubmitted(true);
      // Auto-dismiss the success state after 2s, then reset.
      window.setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setTitle('');
        setDescription('');
        setScreenshotBase64(null);
        setScreenshotName(null);
      }, 2000);
    },
  });

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setScreenshotBase64(result);
      setScreenshotName(file.name);
    };
    reader.readAsDataURL(file);
  };

  // Auto-capture: hide the slide-over via visibility:hidden, html2canvas
  // the document body, then restore. Visibility (not display:none) keeps
  // layout calculations stable so the captured view matches what the
  // user is actually looking at. Dynamic import keeps html2canvas out
  // of the main bundle — only loaded when this button is clicked.
  const handleAutoCapture = async () => {
    if (capturing) return;
    setCaptureError(null);
    setCapturing(true);
    const modal = modalRef.current;
    if (modal) modal.style.visibility = 'hidden';
    // One frame so the browser commits the hide before snapshot.
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    try {
      const html2canvas = (await import('html2canvas')).default;
      // backgroundColor must come from the body — null defaults to
      // black, which makes light-mode captures look broken wherever
      // the DOM is transparent.
      const bodyBg = getComputedStyle(document.body).backgroundColor || '#faf9f7';
      // Capture only the visible viewport, not the whole scroll
      // height — the user is reporting what they SEE. windowHeight +
      // height pin the render to viewport bounds.
      const canvas = await html2canvas(document.body, {
        // Defense-in-depth: even with visibility:hidden on the modal
        // root, skip any descendant that lives inside the feedback
        // overlay (e.g. portaled toasts) — they shouldn't appear in
        // the bug-report screenshot.
        ignoreElements: (el) => el.closest('[data-feedback-modal]') !== null,
        useCORS: true,
        logging: false,
        // Cap scale at 1 on retina to keep the JPEG under the 4MB
        // payload budget. Low-DPI screens already render at 1, so
        // hard-coding 1 is fine across the board.
        scale: 1,
        windowHeight: window.innerHeight,
        height: window.innerHeight,
        backgroundColor: bodyBg,
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setScreenshotBase64(dataUrl);
      setScreenshotName(t('feedback.screenshotAttached'));
    } catch (err) {
      console.error('Screenshot capture failed', err);
      setCaptureError(t('feedback.captureFailed'));
    } finally {
      if (modal) modal.style.visibility = '';
      setCapturing(false);
    }
  };

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="bg-accent shadow-accent/30 fixed right-4 bottom-4 z-40 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105 md:right-6 md:bottom-6"
      >
        <MessageSquare className="h-4 w-4" />
        {t('feedback.button')}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60"
          onClick={() => !submit.isPending && setOpen(false)}
        >
          <div
            ref={modalRef}
            data-feedback-modal
            className="bg-card border-border fixed top-0 right-0 flex h-full w-full max-w-md flex-col border-l shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-border flex items-center justify-between border-b px-5 py-4">
              <h2 className="font-heading text-text-primary text-lg font-bold">
                {t('feedback.title')}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={submit.isPending}
                className="text-text-tertiary hover:text-text-primary"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            {submitted ? (
              <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
                  <Check className="h-8 w-8" />
                </div>
                <p className="font-heading text-text-primary text-lg font-bold">
                  {t('feedback.success.title')}
                </p>
                <p className="text-text-secondary mt-1 text-sm">{t('feedback.success.message')}</p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submit.mutate();
                }}
                className="flex flex-1 flex-col overflow-y-auto px-5 py-4"
              >
                <label className="field-label">{t('feedback.category')}</label>
                <div className="mb-4 grid grid-cols-4 gap-2">
                  {(Object.keys(CATEGORY_META) as Category[]).map((c) => {
                    const Icon = CATEGORY_META[c].icon;
                    const active = c === category;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCategory(c)}
                        className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-xs transition-colors ${
                          active
                            ? 'border-accent bg-accent/10 text-accent'
                            : 'border-border text-text-secondary hover:bg-subtle'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {t(`feedback.category.${c}`)}
                      </button>
                    );
                  })}
                </div>

                <label className="field-label">{t('feedback.titleField')}</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={255}
                  placeholder={t('feedback.titlePlaceholder')}
                  className="field-input mb-4"
                />

                <label className="field-label">{t('feedback.description')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  rows={6}
                  placeholder={t('feedback.descriptionPlaceholder')}
                  className="field-input mb-4 resize-none"
                />

                <label className="field-label">{t('feedback.screenshot')}</label>
                <div className="mb-4">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFile}
                    className="hidden"
                  />
                  {screenshotBase64 ? (
                    <div className="border-border bg-subtle/30 relative overflow-hidden rounded-xl border">
                      <img
                        src={screenshotBase64}
                        alt=""
                        className="h-32 w-full object-cover object-top"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setScreenshotBase64(null);
                          setScreenshotName(null);
                        }}
                        aria-label={t('common.remove')}
                        className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
                      >
                        <XIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="ghost"
                        onClick={handleAutoCapture}
                        disabled={capturing}
                        className="inline-flex min-h-[44px] items-center justify-center gap-1.5 text-sm"
                      >
                        {capturing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Camera className="h-4 w-4" />
                        )}
                        {capturing ? t('feedback.capturing') : t('feedback.captureScreen')}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => fileRef.current?.click()}
                        className="inline-flex min-h-[44px] items-center justify-center gap-1.5 text-sm"
                      >
                        <ImagePlus className="h-4 w-4" />
                        {t('feedback.uploadImage')}
                      </Button>
                    </div>
                  )}
                  {captureError && <p className="mt-2 text-xs text-red-500">{captureError}</p>}
                </div>

                <p className="text-text-tertiary mb-4 text-xs">
                  {t('feedback.pageContext', { path: location.pathname })}
                </p>

                {submit.isError && (
                  <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">
                    {(() => {
                      const status = (submit.error as { response?: { status?: number } })?.response
                        ?.status;
                      if (status === 429) return t('feedback.rateLimited');
                      if (status === 404) return t('feedback.disabled');
                      return t('errors.generic');
                    })()}
                  </div>
                )}

                <div className="border-border mt-auto flex justify-end gap-2 border-t pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setOpen(false)}
                    disabled={submit.isPending}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={submit.isPending || !title.trim() || !description.trim()}
                    className="inline-flex items-center gap-1.5"
                  >
                    {submit.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {t('feedback.submit')}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
